import React from 'react';
import { SvgIconProps } from '@mui/material/SvgIcon';

// Map MUI SVG icon names to Material Icons font names
const iconNameMap: Record<string, string> = {
  // Core navigation and actions
  AccessTimeIcon: 'access_time',
  AddIcon: 'add',
  AddRoadIcon: 'add_road',
  AirplanemodeActiveIcon: 'flight',
  ArrowBackIcon: 'arrow_back',
  ArrowDownwardIcon: 'keyboard_arrow_down',
  ArrowDropDownIcon: 'arrow_drop_down',
  ArrowUpwardIcon: 'keyboard_arrow_up',
  BeachAccessIcon: 'beach_access',
  BoltIcon: 'bolt',
  CheckIcon: 'check',
  ChevronRightIcon: 'chevron_right',
  ChevronLeftIcon: 'chevron_left',
  CleaningServicesIcon: 'cleaning_services',
  ClearIcon: 'clear',
  CloseIcon: 'close',
  CloseFullscreenIcon: 'fullscreen_exit',
  CloudUploadIcon: 'cloud_upload',
  CompassIcon: 'navigation',
  ConnectingAirportsIcon: 'connecting_airports',
  ContentCopyIcon: 'content_copy',
  CopyAllIcon: 'copy_all',
  DeleteIcon: 'delete',
  DoneIcon: 'done',
  DownloadIcon: 'download',
  EditIcon: 'edit',
  EditNoteIcon: 'edit_note',
  ExpandLessIcon: 'expand_less',
  ExpandMoreIcon: 'expand_more',
  FilledFlagIcon: 'flag',
  FlightIcon: 'flight',
  FlightLandIcon: 'flight_land',
  FlightTakeoffIcon: 'flight_takeoff',
  GpsFixedIcon: 'gps_fixed',
  GridViewIcon: 'grid_view',
  GroupIcon: 'group',
  HelpIcon: 'help',
  HelpCenterIcon: 'help_center',
  HelpOutlineIcon: 'help_outline',
  ImageAspectRatioIcon: 'aspect_ratio',
  InfoIcon: 'info',
  KeyboardDoubleArrowLeftIcon: 'keyboard_double_arrow_left',
  KeyboardDoubleArrowRightIcon: 'keyboard_double_arrow_right',
  LabelIcon: 'label',
  LinearScaleIcon: 'linear_scale',
  LinkIcon: 'link',
  LocalFireDepartmentIcon: 'local_fire_department',
  LockIcon: 'lock',
  LockOpenIcon: 'lock_open',
  LogoutIcon: 'logout',
  MailOutlineIcon: 'mail_outline',
  MapIcon: 'map',
  MoreHorizIcon: 'more_horiz',
  MoreVertIcon: 'more_vert',
  MyLocationIcon: 'my_location',
  NextIcon: 'navigate_next',
  NearMeIcon: 'near_me',
  OpenInFullIcon: 'open_in_full',
  OutlinedFlagIcon: 'outlined_flag',
  PictureInPictureIcon: 'picture_in_picture',
  PlayIcon: 'play_arrow',
  PrintIcon: 'print',
  RadioButtonUncheckedIcon: 'radio_button_unchecked',
  RefreshIcon: 'refresh',
  RemoveIcon: 'remove',
  ReplayIcon: 'replay',
  RocketLaunchIcon: 'rocket_launch',
  SaveIcon: 'save',
  ScatterPlotIcon: 'scatter_plot',
  SearchIcon: 'search',
  SettingsIcon: 'settings',
  SpeedIcon: 'speed',
  SquareFootIcon: 'square_foot',
  StopIcon: 'stop',
  StraightenIcon: 'straighten',
  SwitchAccountIcon: 'switch_account',
  SwitchLeftIcon: 'switch_left_alt',
  TextFieldsIcon: 'text_fields',
  TextureIcon: 'texture',
  ThumbUpIcon: 'thumb_up',
  TitleIcon: 'title',
  TuneIcon: 'tune',
  UnfoldLessIcon: 'unfold_less',
  WarningIcon: 'warning',
  WidgetsIcon: 'widgets',
  CalculateIcon: 'calculate',
  MonetizationOnIcon: 'monetization_on',
  SupportIcon: 'support',
  // Additional icons
  CameraAltIcon: 'camera_alt',
  ExpandCircleDownIcon: 'expand_circle_down',
  FilterListIcon: 'filter_list',
  GetAppIcon: 'get_app',
  LaunchIcon: 'launch',
  MinimizeIcon: 'minimize',
  NotificationImportantIcon: 'notification_important',
  PauseCircleIcon: 'pause_circle',
  PhoneIcon: 'phone',
  PhotoCameraIcon: 'photo_camera',
  PlaceIcon: 'place',
  PublicIcon: 'public',
  QuestionMarkIcon: 'help',
  ScheduleIcon: 'schedule',
  SmartToyIcon: 'smart_toy',
  SyncAltIcon: 'sync_alt',
  TimelineIcon: 'timeline',
  TransformIcon: 'transform',
  VisibilityIcon: 'visibility',
  VisibilityOffIcon: 'visibility_off',
  ZoomInIcon: 'zoom_in',
  ZoomOutIcon: 'zoom_out',
  StarIcon: 'star',
  StarBorderIcon: 'star_border',
  ViewModuleIcon: 'view_module',
  ViewComfyIcon: 'view_comfy',
  AnalyticsIcon: 'analytics',
  InsightsIcon: 'insights',
  PersonIcon: 'person',
  BugReportIcon: 'bug_report',
  BuildIcon: 'build',
  CalendarMonthIcon: 'calendar_month',
  NavigateNextIcon: 'navigate_next',
  ShareIcon: 'share',
  TableViewIcon: 'table_view',
  TodayIcon: 'today',
  EventBusyIcon: 'event_busy',
};

export interface CdnIconProps extends React.HTMLAttributes<HTMLSpanElement> {
  name: keyof typeof iconNameMap;
  fontSize?: SvgIconProps['fontSize'] | number;
  color?: SvgIconProps['color'];
}

/**
 * CDN-based icon component using Material Icons font.
 * Zero bundle impact - all icons loaded from Google Fonts CDN (42KB total).
 * Replaces individual MUI SVG imports with a single font file.
 */
export const CdnIcon = React.forwardRef<HTMLSpanElement, CdnIconProps>(({ 
  name, 
  fontSize = 'medium',
  color = 'inherit',
  
  className,
  style,
  ...props 
}, ref) => {
  const fontIconName = iconNameMap[name];
  
  if (!fontIconName) {
    console.warn(`CdnIcon: Unknown icon "${name}"`);
    return <span ref={ref} className={`material-icons ${className || ''}`} style={style}>help</span>;
  }

  // Convert MUI fontSize to CSS size
  const getFontSize = (size: string | number) => {
    if (typeof size === 'number') return `${size}px`;
    switch (size) {
      case 'small': return '20px';
      case 'medium': return '24px'; 
      case 'large': return '36px';
      case 'inherit': return 'inherit';
      default: return size;
    }
  };

  // Convert MUI color to CSS color
  const getColor = (colorProp: string) => {
    switch (colorProp) {
      case 'inherit': return 'inherit';
      case 'primary': return 'var(--mui-palette-primary-main, #1976d2)';
      case 'secondary': return 'var(--mui-palette-secondary-main, #dc004e)';
      case 'error': return 'var(--mui-palette-error-main, #d32f2f)';
      case 'warning': return 'var(--mui-palette-warning-main, #ed6c02)';
      case 'info': return 'var(--mui-palette-info-main, #0288d1)';
      case 'success': return 'var(--mui-palette-success-main, #2e7d32)';
      case 'action': return 'var(--mui-palette-action-active, rgba(0, 0, 0, 0.54))';
      case 'disabled': return 'var(--mui-palette-action-disabled, rgba(0, 0, 0, 0.26))';
      default: return colorProp;
    }
  };

  const combinedStyle = {
    fontSize: getFontSize(fontSize),
    color: getColor(color),
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    verticalAlign: 'middle',
    lineHeight: 1,
    ...style,
  };

  return (
    <span 
      ref={ref}
      className={`material-icons ${className || ''}`}
      style={combinedStyle}
      {...props}
    >
      {fontIconName}
    </span>
  );
});

CdnIcon.displayName = 'CdnIcon';

export default CdnIcon;